import type { TFunction } from 'i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CampaignRounded,
  CheckCircleRounded,
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  syllabusProposalApi,
  type SyllabusProposalDto,
} from '../../../lib/api/course-template.api';
import { errorText } from '../../../lib/error-text';

/** Etiqueta legible del campo propuesto. Los paths de módulo llevan el id de
 *  la plantilla adentro, que al académico no le dice nada. */
function fieldLabel(
  p: SyllabusProposalDto,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (p.kind === 'module') {
    return p.path.endsWith('.axisTopic')
      ? t('syllabusProposals.fields.moduleAxis')
      : t('syllabusProposals.fields.moduleName');
  }
  return t(`syllabusProposals.fields.${p.path}`, { defaultValue: p.path });
}

/** Los valores propuestos son de formas muy distintas (texto, TipTap, listas de
 *  lecturas, un sí/no, descripciones por evaluación). Se muestra una lectura
 *  legible, no un JSON crudo. */
function preview(value: unknown, t: TFunction<'sis'>): string {
  if (value === null || value === undefined || value === '')
    return t('syllabusProposals.preview.empty');
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean')
    return value ? t('syllabusProposals.preview.yes') : t('syllabusProposals.preview.no');
  if (Array.isArray(value)) return t('syllabusProposals.preview.items', { count: value.length });
  if (typeof value === 'object') {
    const asDoc = value as { type?: string; content?: unknown[] };
    if (asDoc.type === 'doc') return tipTapText(asDoc);
    // `grading.descriptions`: evaluación → su texto (o null, quitada).
    const entries = Object.values(value as Record<string, unknown>);
    if (entries.every((v) => v === null || (v as { type?: string })?.type === 'doc')) {
      return entries
        .map((v) => (v === null ? t('syllabusProposals.preview.removed') : tipTapText(v)))
        .join(' · ');
    }
    return JSON.stringify(value).slice(0, 400);
  }
  return String(value);
}

function tipTapText(node: unknown): string {
  const n = node as { text?: string; content?: unknown[] };
  if (typeof n.text === 'string') return n.text;
  return (n.content ?? []).map(tipTapText).join(' ').trim();
}

export function SyllabusProposalsInbox(): JSX.Element | null {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [observations, setObservations] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['syllabus-proposals', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: () => syllabusProposalApi.list({ tenantId: currentTenantId!, status: 'submitted' }),
  });

  const pending = q.data?.proposals ?? [];
  // Sin propuestas abiertas la bandeja no existe: no se le ocupa espacio al
  // académico con una caja vacía.
  if (!currentTenantId || pending.length === 0) return null;

  const decide = async (p: SyllabusProposalDto, decision: 'approved' | 'rejected') => {
    setBusy(true);
    setError(null);
    try {
      await syllabusProposalApi.decide({
        tenantId: currentTenantId,
        proposalId: p.id,
        decision,
        observations,
      });
      setOpenId(null);
      setObservations('');
      await queryClient.invalidateQueries({ queryKey: ['syllabus-proposals', currentTenantId] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'info.main' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <CampaignRounded color="info" />
        <Typography variant="subtitle1" fontWeight={700}>
          {t('syllabusProposals.title')}
        </Typography>
        <Chip size="small" color="info" label={pending.length} />
        <Box flex={1} />
        <IconButton size="small">
          {expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5} mb={1.5}>
          {t('syllabusProposals.subtitle')}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={1}>
          {pending.map((p) => {
            const isOpen = openId === p.id;
            return (
              <Paper key={p.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip size="small" label={fieldLabel(p, t as never)} />
                  <Typography variant="body2" fontWeight={600}>
                    {p.sectionLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('syllabusProposals.proposedBy', {
                      name: p.proposedByName,
                      date: p.submittedAt.slice(0, 10),
                    })}
                  </Typography>
                  <Box flex={1} />
                  <Button
                    size="small"
                    onClick={() => {
                      setOpenId(isOpen ? null : p.id);
                      setObservations('');
                    }}
                  >
                    {isOpen ? t('syllabusProposals.hide') : t('syllabusProposals.review')}
                  </Button>
                </Stack>

                <Collapse in={isOpen}>
                  <Stack spacing={1.5} mt={1.5}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        {t('syllabusProposals.current')}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {preview(p.currentValue, t)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="overline" color="info.main">
                        {t('syllabusProposals.proposed')}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {preview(p.proposedValue, t)}
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      multiline
                      minRows={2}
                      fullWidth
                      label={t('syllabusProposals.observations')}
                      value={observations}
                      onChange={(e) => setObservations(e.target.value)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        color="error"
                        startIcon={<CloseRounded />}
                        disabled={busy || observations.trim() === ''}
                        onClick={() => void decide(p, 'rejected')}
                      >
                        {t('syllabusProposals.reject')}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleRounded />}
                        disabled={busy}
                        onClick={() => void decide(p, 'approved')}
                      >
                        {t('syllabusProposals.approve')}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t('syllabusProposals.approveNote')}
                    </Typography>
                  </Stack>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      </Collapse>
    </Paper>
  );
}
